// src/pages/CreateTontine.jsx — create a new tontine group
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { ArrowRight, CircleDollarSign, Info } from "lucide-react";

const groupSizeByCategory = {
  student: { min: 10, max: 20 },
  salaried_diaspora: { min: 10, max: 15 },
  community_family: { min: 5, max: 10 },
  entrepreneur_business: { min: 5, max: 10 },
  etudiant: { min: 10, max: 20 },
  salarie: { min: 10, max: 15 },
  entrepreneur: { min: 5, max: 10 },
};

const CreateTontine = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "salaried_diaspora",
    amount: 50,
    currency: "eur",
    frequency: "monthly",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const groupSize = groupSizeByCategory[form.category];

  const handleChange = (e) => {
    setSuggestion(null);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSuggestName = async () => {
    setSuggesting(true);
    setError("");
    try {
      const { data } = await api.get("/tontines/name-suggestion", {
        params: { category: form.category, exclude: form.name },
      });
      setSuggestion(data);
    } catch (err) {
      setError(err.response?.data?.message || t("common.error"));
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((current) => ({
      ...current,
      name: suggestion.name,
      description: suggestion.description,
      amount: suggestion.amount,
    }));
    setSuggestion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/tontines", {
        ...form,
        amount: Number(form.amount),
      });
      navigate(`/tontines/${data._id}`);
    } catch (err) {
      setError(err.response?.data?.message || t("common.error"));
      if (err.response?.data?.suggestedCircle) setSuggestion(err.response.data.suggestedCircle);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-shell max-w-4xl">
      <p className="eyebrow mb-2">Build together</p>
      <h1 className="mb-2 font-serif text-3xl font-bold text-gold-700">{t("nav.createTontine")}</h1>
      <p className="mb-8 max-w-xl text-sm leading-6 text-gray-500">Set up a trusted circle and invite your community to save together.</p>

      {error && <p className="mb-5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <div className="mb-6 grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <form onSubmit={handleSubmit} className="card space-y-6 p-6 sm:p-8">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              name="name"
              maxLength={100}
              value={form.name}
              onChange={handleChange}
              className="input"
              placeholder="Example: Team Growth Circle"
            />
            <p className="mt-1 text-xs text-gray-500">Circle names must be unique across the platform.</p>
            <button type="button" onClick={handleSuggestName} disabled={suggesting} className="mt-2 text-sm font-semibold text-gold-700 underline disabled:opacity-50">
              {suggesting ? t("common.loading") : "Suggest a unique name"}
            </button>
            {suggestion && <p className="mt-2 text-sm text-emerald-800" aria-live="polite">Suggested name: <button type="button" className="font-semibold underline" onClick={applySuggestion}>{suggestion.name} — use suggestion</button></p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              name="description"
              rows={3}
              required={Boolean(form.name.trim())}
              maxLength={500}
              value={form.description}
              onChange={handleChange}
              className="input"
              placeholder="Describe your savings goals, rules, and expectations for members."
            />
          </div>

          <div>
            <label className="label">{t("tontine.category")}</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="input"
            >
              <option value="student">{t("tontine.categories.student")}</option>
              <option value="salaried_diaspora">{t("tontine.categories.salaried_diaspora")}</option>
              <option value="community_family">{t("tontine.categories.community_family")}</option>
              <option value="entrepreneur_business">{t("tontine.categories.entrepreneur_business")}</option>
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="label">{t("tontine.amount")}</label>
              <input
                type="number"
                name="amount"
                min={1}
                required
                value={form.amount}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t("tontine.frequency")}</label>
              <select
                name="frequency"
                value={form.frequency}
                onChange={handleChange}
                className="input"
              >
                <option value="weekly">{t("common.frequency.weekly") || "Hebdomadaire"}</option>
                <option value="biweekly">{t("common.frequency.biweekly") || "Toutes les 2 semaines"}</option>
                <option value="monthly">{t("common.frequency.monthly") || "Mensuel"}</option>
              </select>
            </div>
            <div>
              <label className="label">Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className="input">
                <option value="eur">EUR — Euro</option>
                <option value="usd">USD — US Dollar</option>
                <option value="cad">CAD — Canadian Dollar</option>
                <option value="gbp">GBP — British Pound</option>
                <option value="aud">AUD — Australian Dollar</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">USDT and other cryptocurrencies are not supported by Stripe Checkout.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl bg-gold-50/70 p-4 text-xs leading-5 text-gray-600">
            <Info size={16} className="mt-0.5 shrink-0 text-gold-600" />
            Your category determines the circle's trust and membership requirements. The circle starts when it reaches {groupSize.min} members; joining then closes. Contribution amounts are set by the circle creator.
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? t("common.loading") : <><CircleDollarSign size={17} /> {t("nav.createTontine")} <ArrowRight size={16} /></>}
          </button>
        </form>

        <aside className="card h-fit p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-700">Circle preview</p>
          <h2 className="mt-3 font-serif text-2xl font-bold text-forest">
            {form.name || "Your new tontine"}
          </h2>

          <div className="mt-5 space-y-4 text-sm text-gray-600">
            <div className="rounded-xl bg-gold-50 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-gold-700">Contribution</p>
              <p className="mt-2 text-lg font-bold text-forest">{form.currency.toUpperCase()} {form.amount || 0} / {form.frequency}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Expected rhythm</p>
              <p className="mt-2 font-semibold text-emerald-800">
                {form.frequency === "weekly" ? "Weekly collection and visibility" : form.frequency === "bimonthly" ? "Bi-monthly contributions" : "Monthly cycle management"}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Group size</p>
              <p className="mt-2 font-semibold text-forest">{groupSize.min}–{groupSize.max} members</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                At {groupSize.min}, the circle becomes active and no longer accepts new members.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Description</p>
              <p className="mt-2 leading-6 text-gray-600">
                {form.description || "A clear, community-first savings circle with shared goals and predictable payouts."}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateTontine;
